import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Paper, TextField, Button, Avatar, MenuItem, InputLabel, FormControl, Select } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import BaseUrl from '../Api/baseurl';

const EditUserProfile = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    username: Cookies.get('username') || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    profile_picture: '',
    gender: '',
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formErrors, setFormErrors] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    gender: '',
    profile_picture: '',
  });

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountryData = async () => {
      try {
        const restCountriesResponse = await fetch('https://restcountries.com/v3.1/all');
        const restCountriesData = await restCountriesResponse.json();
        const geoNamesResponse = await axios.get(`${BaseUrl}/proxy/geo-names/`);
        const geoNamesData = geoNamesResponse.data;

        const geoNamesMap = {};
        geoNamesData.geonames.forEach((country) => {
          geoNamesMap[country.countryName] = country.geonameId;
        });

        const combinedData = restCountriesData.map((country) => ({
          name: country.name.common,
          geonameId: geoNamesMap[country.name.common] || null,
        }));

        combinedData.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(combinedData);
      } catch (error) {
        console.error('Error fetching country data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load country data.',
        });
      }
    };
    fetchCountryData();
  }, []);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      const token = Cookies.get('token');
      if (!token) {
        Swal.fire({
          icon: 'warning',
          title: 'Please login to continue.',
        });
        navigate('/user/login');
        return;
      }

      try {
        const response = await axios.get(`${BaseUrl}/userdetails/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        // Sanitize API response to prevent undefined/null
        setUserData((prev) => ({
          ...prev,
          ...Object.keys(prev).reduce((acc, key) => {
            acc[key] = response.data[key] ?? prev[key];
            return acc;
          }, {}),
        }));
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.response?.status === 401) {
          Swal.fire({
            icon: 'warning',
            title: 'Session expired. Please login again.',
          });
          Cookies.remove('token');
          Cookies.remove('username');
          Cookies.remove('is_user');
          setIsLoggedIn(false);
          navigate('/user/login');
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load profile data.',
          });
        }
      }
    };
    fetchUserData();
  }, [navigate, setIsLoggedIn]);

  // Fetch states when country changes
  const fetchStates = (country) => {
    if (country && country.geonameId) {
      axios
        .get(`https://secure.geonames.org/childrenJSON?geonameId=${country.geonameId}&username=bhavyalogicspice`)
        .then((response) => {
          const stateNames = response.data.geonames.map((state) => state.name);
          setStates(stateNames);
        })
        .catch((error) => {
          console.error('Error fetching states:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load states.',
          });
        });
    }
  };

  // Fetch cities when state changes
  const fetchCities = (stateName) => {
    if (stateName) {
      axios
        .get(`https://secure.geonames.org/searchJSON?name=${stateName}&featureCode=ADM1&username=bhavyalogicspice`)
        .then((response) => {
          if (response.data.geonames && response.data.geonames.length > 0) {
            const stateGeoId = response.data.geonames[0].geonameId;
            axios
              .get(`https://secure.geonames.org/childrenJSON?geonameId=${stateGeoId}&username=bhavyalogicspice`)
              .then((cityResponse) => {
                const cityNames = cityResponse.data.geonames.map((city) => city.name);
                setCities(cityNames);
              })
              .catch((error) => {
                console.error('Error fetching cities:', error);
                Swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to load cities.',
                });
              });
          }
        })
        .catch((error) => {
          console.error('Error fetching state GeoNameId:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load state data.',
          });
        });
    }
  };

  // Update states when country changes or is loaded
  useEffect(() => {
    if (userData.country && countries.length > 0) {
      const selectedCountry = countries.find((country) => country.name === userData.country);
      if (selectedCountry) {
        fetchStates(selectedCountry);
      }
    }
  }, [userData.country, countries]);

  // Update cities when state changes or is loaded
  useEffect(() => {
    if (userData.state && states.length > 0) {
      fetchCities(userData.state);
    }
  }, [userData.state, states]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
    setFormErrors({
      ...formErrors,
      [name]: '',
    });
  };

  const handleCountryChange = (e) => {
    const country = JSON.parse(e.target.value);
    setUserData({
      ...userData,
      country: country.name,
      state: '',
      city: '',
    });
    setFormErrors({
      ...formErrors,
      country: '',
      state: '',
      city: '',
    });
    setStates([]);
    setCities([]);
    fetchStates(country);
  };

  const handleStateChange = (e) => {
    const stateName = e.target.value;
    setUserData({
      ...userData,
      state: stateName,
      city: '',
    });
    setFormErrors({
      ...formErrors,
      state: '',
      city: '',
    });
    setCities([]);
    fetchCities(stateName);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormErrors({
        ...formErrors,
        profile_picture: 'Please select an image file.',
      });
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setFormErrors({
        ...formErrors,
        profile_picture: 'File size exceeds 2MB.',
      });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFormErrors({
        ...formErrors,
        profile_picture: 'Only JPEG or PNG images are allowed.',
      });
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const maxWidth = 2000;
      const maxHeight = 2000;

      if (img.width > maxWidth || img.height > maxHeight) {
        setFormErrors({
          ...formErrors,
          profile_picture: `Image dimensions exceed ${maxWidth}x${maxHeight} pixels.`,
        });
      } else {
        setProfilePictureFile(file);
        setUserData({
          ...userData,
          profile_picture: URL.createObjectURL(file),
        });
        setFormErrors({
          ...formErrors,
          profile_picture: '',
        });
      }
    };

    img.onerror = () => {
      setFormErrors({
        ...formErrors,
        profile_picture: 'Error loading image.',
      });
    };

    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    let isValid = true;
    const errors = { ...formErrors };

    // Username
    if (!(userData.username || '').trim()) {
      errors.username = 'Username is required.';
      isValid = false;
    } else if (!/^[a-zA-Z0-9_-]{3,30}$/.test((userData.username || '').trim())) {
      errors.username = 'Username must be 3-30 characters (alphanumeric, underscores, or hyphens).';
      isValid = false;
    }

    // First Name
    if (!(userData.first_name || '').trim()) {
      errors.first_name = 'First Name is required.';
      isValid = false;
    } else if (!/^[a-zA-Z\s-]{2,50}$/.test((userData.first_name || '').trim())) {
      errors.first_name = 'First Name must be 2-50 characters (letters, spaces, or hyphens).';
      isValid = false;
    }

    // Last Name
    if (!(userData.last_name || '').trim()) {
      errors.last_name = 'Last Name is required.';
      isValid = false;
    } else if (!/^[a-zA-Z\s-]{2,50}$/.test((userData.last_name || '').trim())) {
      errors.last_name = 'Last Name must be 2-50 characters (letters, spaces, or hyphens).';
      isValid = false;
    }

    // Email
    if (!(userData.email || '').trim()) {
      errors.email = 'Email is required.';
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test((userData.email || '').trim())) {
      errors.email = 'Please enter a valid email address.';
      isValid = false;
    }

    // Phone Number
    if (!(userData.phone_no || '').trim()) {
      errors.phone_no = 'Phone Number is required.';
      isValid = false;
    } else if (!/^\d{10}$/.test((userData.phone_no || '').trim())) {
      errors.phone_no = 'Phone Number must be exactly 10 digits.';
      isValid = false;
    }

    // Address
    if (!(userData.address || '').trim()) {
      errors.address = 'Address is required.';
      isValid = false;
    } else if (!/^[\w\s,.-]{5,200}$/.test((userData.address || '').trim())) {
      errors.address = 'Address must be 5-200 characters (letters, numbers, spaces, commas, periods, or hyphens).';
      isValid = false;
    }

    // Country
    if (!(userData.country || '').trim()) {
      errors.country = 'Country is required.';
      isValid = false;
    }

    // State
    if (!(userData.state || '').trim()) {
      errors.state = 'State is required.';
      isValid = false;
    }

    // City
    if (!(userData.city || '').trim()) {
      errors.city = 'City is required.';
      isValid = false;
    }

    // Zipcode
    if (!(userData.zipcode || '').trim()) {
      errors.zipcode = 'Zipcode is required.';
      isValid = false;
    } else if (!/^\d{5}(\d{1,2})?$/.test((userData.zipcode || '').trim())) {
      errors.zipcode = 'Zipcode must be 5-7 digits.';
      isValid = false;
    }

    // Gender
    if (!(userData.gender || '').trim()) {
      errors.gender = 'Gender is required.';
      isValid = false;
    }

    // Profile Picture
    if (!(userData.profile_picture || '') && !profilePictureFile) {
      errors.profile_picture = 'Profile Picture is required.';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    const token = Cookies.get('token');
  
    if (!validateForm()) {
      return;
    }
  
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to save the changes to your profile?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, save it!',
    });
  
    if (!result.isConfirmed) {
      return; // User cancelled the confirmation
    }
  
    const formData = new FormData();
    formData.append('username', userData.username);
    formData.append('first_name', userData.first_name);
    formData.append('last_name', userData.last_name);
    formData.append('email', userData.email);
    formData.append('phone_no', userData.phone_no);
    formData.append('address', userData.address);
    formData.append('city', userData.city);
    formData.append('state', userData.state);
    formData.append('country', userData.country);
    formData.append('zipcode', userData.zipcode);
    formData.append('gender', userData.gender);
    if (profilePictureFile) {
      formData.append('profile_picture', profilePictureFile);
    }
  
    try {
      const response = await axios.put(`${BaseUrl}/userdetails/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      Swal.fire({
        icon: 'success',
        title: 'Profile updated successfully!',
      });
      navigate('/user/myprofile');
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to update profile: ${error.response?.data?.message || error.message}`,
      });
    }
  };
  

  const handleCancel = () => {
    navigate('/user/myprofile');
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 w-full">
      <Paper elevation={3} className="p-8 rounded-xl bg-white w-full">
        <Typography
          variant="h4"
          className="font-bold mb-6"
          style={{ color: '#212529' }}
        >
          Edit Profile
        </Typography>

        <div className="flex flex-col gap-8">
          {/* Profile Picture */}
          <div className="flex flex-col items-center">
            <Avatar
              src={userData.profile_picture}
              sx={{ width: 160, height: 160 }}
              className="border-4 border-[#BF9455] shadow-md mb-4"
            />
            <Button
              variant="outlined"
              component="label"
              style={{ borderColor: '#212529', color: '#212529' }}
              className="hover:bg-[#BF9455] hover:!text-white"
              aria-label="Upload profile picture"
            >
              Upload New Photo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <span className="text-red-500 mt-2 text-sm">{formErrors.profile_picture}</span>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* <TextField
              label="Username"
              name="username"
              value={userData.username}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } ,readOnly: true,}}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.username}
              helperText={formErrors.username}
            /> */}
            <TextField
              label="First Name"
              name="first_name"
              value={userData.first_name}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.first_name}
              helperText={formErrors.first_name}
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={userData.last_name}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.last_name}
              helperText={formErrors.last_name}
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={userData.email}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              label="Phone Number"
              name="phone_no"
              value={userData.phone_no}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                if (digitsOnly.length <= 10) {
                  handleInputChange({ target: { name: 'phone_no', value: digitsOnly } });
                }
              }}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.phone_no}
              helperText={formErrors.phone_no}
            />
            <TextField
              label="Address"
              name="address"
              value={userData.address}
              onChange={handleInputChange}
              fullWidth
              required
              multiline
              rows={2}
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />
            <FormControl fullWidth variant="outlined" error={!!formErrors.country}>
              <InputLabel style={{ color: '#212529' }}>Country</InputLabel>
              <Select
                name="country"
                value={
                  countries.find((country) => country.name === userData.country)
                    ? JSON.stringify(countries.find((country) => country.name === userData.country))
                    : ''
                }
                onChange={handleCountryChange}
                label="Country"
                style={{ color: '#212529' }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#212529' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                }}
              >
                <MenuItem value="">Select Country</MenuItem>
                {countries.map((country, index) => (
                  <MenuItem key={index} value={JSON.stringify(country)}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.country && (
                <Typography variant="caption" color="error">
                  {formErrors.country}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth variant="outlined" error={!!formErrors.state}>
              <InputLabel style={{ color: '#212529' }}>State</InputLabel>
              <Select
                name="state"
                value={userData.state}
                onChange={handleStateChange}
                label="State"
                style={{ color: '#212529' }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#212529' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                }}
              >
                <MenuItem value="">Select State</MenuItem>
                {states.map((state, index) => (
                  <MenuItem key={index} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.state && (
                <Typography variant="caption" color="error">
                  {formErrors.state}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth variant="outlined" error={!!formErrors.city}>
              <InputLabel style={{ color: '#212529' }}>City</InputLabel>
              <Select
                name="city"
                value={userData.city}
                onChange={handleInputChange}
                label="City"
                style={{ color: '#212529' }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#212529' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                }}
              >
                <MenuItem value="">Select City</MenuItem>
                {cities.map((city, index) => (
                  <MenuItem key={index} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.city && (
                <Typography variant="caption" color="error">
                  {formErrors.city}
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Zipcode"
              name="zipcode"
              value={userData.zipcode}
              onChange={handleInputChange}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: '#212529' } }}
              InputProps={{ style: { color: '#212529' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#212529' },
                  '&:hover fieldset': { borderColor: '#BF9455' },
                  '&.Mui-focused fieldset': { borderColor: '#BF9455' },
                },
              }}
              error={!!formErrors.zipcode}
              helperText={formErrors.zipcode}
            />
            <FormControl fullWidth variant="outlined" error={!!formErrors.gender}>
              <InputLabel style={{ color: '#212529' }}>Gender</InputLabel>
              <Select
                name="gender"
                value={userData.gender}
                onChange={handleInputChange}
                label="Gender"
                style={{ color: '#212529' }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#212529' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#BF9455' },
                }}
              >
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
              {formErrors.gender && (
                <Typography variant="caption" color="error">
                  {formErrors.gender}
                </Typography>
              )}
            </FormControl>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outlined"
              style={{ borderColor: '#212529', color: '#212529' }}
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              className="hover:bg-[#BF9455] hover:!text-white"
              aria-label="Cancel changes"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#BF9455', color: '#FFFFFF' }}
              startIcon={<SaveIcon />}
              onClick={handleSave}
              className="hover:!bg-[#A07B3F]"
              aria-label="Save changes"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default EditUserProfile;